import React, { useEffect } from "react";
import { useSeo } from "../lib/seo";

const PROFILE_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ProfilePage",
      "@id": "https://brandandbehavior.com/wilhelm-noriega#profilepage",
      "url": "https://brandandbehavior.com/wilhelm-noriega",
      "name": "Wilhelm Noriega - Brand Strategy Advisor & Marketing Director",
      "isPartOf": { "@id": "https://brandandbehavior.com/#website" },
      "mainEntity": { "@id": "https://brandandbehavior.com/wilhelm-noriega#person" },
    },
    {
      "@type": "Person",
      "@id": "https://brandandbehavior.com/wilhelm-noriega#person",
      "name": "Wilhelm Noriega",
      "url": "https://brandandbehavior.com/wilhelm-noriega",
      "jobTitle": "Brand Strategy Advisor & Marketing Director",
      "description": "Wilhelm Noriega is a senior brand strategist and creative director with 15+ years of experience across sport, lifestyle, fashion and consumer brands. He is the founder of Brand & Behavior, a productized platform for brand strategy and brand development.",
      "sameAs": ["https://www.linkedin.com/in/wilhelmnoriega"],
      "worksFor": { "@id": "https://brandandbehavior.com/#organization" },
      "knowsAbout": [
        "Brand Strategy",
        "Creative Direction",
        "Brand Development",
        "Brand Positioning",
        "Brand Architecture",
        "Brand Management",
        "Go-to-Market Strategy",
        "Sport Brands",
        "Lifestyle Brands",
        "Luxury Brands",
      ],
    },
  ],
};

function useProfileJsonLd() {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.jsonldWilhelm = "1";
    el.textContent = JSON.stringify(PROFILE_JSONLD);
    document.head.appendChild(el);
    return () => { el.parentNode && el.parentNode.removeChild(el); };
  }, []);
}

const MODULES = [
  {
    n: "I",
    title: "Cultural Intelligence & Trends",
    tag: "Where strategy begins",
    p1: "Every brief starts with culture, not a moodboard. I read trends as emotional and behavioral signals: where desire is moving, how people feel, what they will need next. I translate them into opportunities.",
    p2: "My practice combines original research with continuous cultural intelligence across fashion, sport, wellness, technology and emerging consumer behavior.",
    imgs: [{ n: 3, alt: "Image 03", img: "/images/wilhelm/03.jpg" }, { n: 4, alt: "Image 04", img: "/images/wilhelm/04.jpg" }],
  },
  {
    n: "II",
    title: "Brand DNA & Positioning",
    tag: "The operating system",
    p1: "Before any expression, I define the internal code: purpose, positioning, naming, architecture. A compact set of principles that lets hundreds of future decisions stay coherent. Strong DNA gives a brand consistency without repetition.",
    p2: "This is the protection of the brand: when the DNA is clear, every campaign, product, space and post strengthens the same idea instead of starting a new one.",
    imgs: [{ n: 5, alt: "Image 05", img: "/images/wilhelm/05.jpg" }, { n: 6, alt: "Image 06", img: "/images/wilhelm/06.jpg" }],
  },
  {
    n: "III",
    title: "Brand World & Universe",
    tag: "Worlds, not campaigns",
    dark: true,
    p1: "People rarely remember advertisements. They remember worlds: recognizable universes where product, editorial, retail and experience belong to one story. I build that universe first, so every campaign becomes a chapter instead of a restart.",
    p2: "The strongest universes turn products into symbols: objects and moments that carry emotion, that people keep, gift and remember.",
    imgs: [{ n: 7, alt: "Image 07", img: "/images/wilhelm/07.webp" }, { n: 8, alt: "Image 08", img: "/images/wilhelm/08.png" }],
  },
  {
    n: "IV",
    title: "Brand Expression, Campaigns, Experience",
    tag: "Image, product, story",
    p1: "From campaigns and product storytelling to visual identity and brand experiences, I shape how brands express themselves across markets, channels and cultural contexts.",
    p2: "I lead creative teams and external partners from concept to execution, aligning creative ambition with brand positioning and business objectives. The goal is not simply to create compelling work, but to build a distinctive, coherent and lasting brand presence.",
    imgs: [{ n: 9, alt: "Brand illustrations", img: "/images/wilhelm/09.jpg" }, { n: 10, alt: "Brand experience event", img: "/images/wilhelm/10.jpg" }],
  },
];

const BELIEF_QUOTE = "Great brands are not reinvented every season. They accumulate meaning. Repeated codes, rituals and points of view create memory, recognition and eventually desirability.";

const BELIEFS = [
  { n: "01", h: "Brand before content", p: "People are tired of brands that publish beautiful posts and reels without a brand behind them: without DNA, without emotion, without understanding their consumer. Content without brand is noise. Brand gives content a reason to exist." },
  { n: "02", h: "Emotions over demographics", p: "The next stage of consumer behavior will be defined less by demographics and more by emotions. Understanding how people feel is now as important as understanding how they spend. Brands that design for emotions build loyalty that data alone cannot explain." },
  { n: "03", h: "Judgment in the era of AI", p: "AI makes output infinite. What it cannot generate is judgment: knowing which direction is right for this brand, this culture, this moment. Direction, taste and decision become the scarce skills." },
  { n: "04", h: "The wide view", p: "Fashion, wellness, longevity and experience are no longer separate industries: they can converge inside one brand and one life. Seeing the whole landscape is what allows precise focus on one project, one category, one decision." },
  { n: "05", h: "Experiences that restore", p: "In an overstimulated world, people seek balance, presence and things that feel made by humans. Brands that slow down, honor craft and engage the senses will win attention that no algorithm can buy." },
];

const W = "/images/wilhelm/work";

const CASES = [
  { n: 15, name: "Brands", meta: "Corporate identity · Brand DNA · Positioning", img: `${W}/brands.jpg`, fit: "contain", bg: "#1c140c" },
  { n: 16, name: "Experiences", meta: "Retail spaces · Events · Physical brand touchpoints", img: `${W}/experiences.jpg` },
  { n: 17, name: "Art Direction", meta: "Editorial · Cultural trend research systems", img: `${W}/art-direction.jpg`, pos: "center 30%" },
  { n: 18, name: "Campaigns", meta: "Sport & lifestyle campaigns", img: `${W}/campaigns.jpg` },
  { n: 19, name: "Retail", meta: "Store concepts · Merchandising · Consumer journeys", img: `${W}/retail.jpg`, fit: "contain", bg: "#17110f" },
  { n: 20, name: "Product Direction", meta: "Football & sport product direction", img: `${W}/product-direction.jpg` },
  { n: 21, name: "Digital", meta: "Websites · Apps · Digital brand systems", stack: [`${W}/digital-1.jpg`, `${W}/digital-2.jpg`] },
  { n: 23, name: "Market & Consumers", meta: "Trend research · Consumer insight", img: `${W}/market-consumers.jpg` },
  { n: 24, name: "Marketing Strategy", meta: "Sell-out analysis · Go-to-market · Results", img: `${W}/marketing-strategy.jpg`, fit: "contain", bg: "#0c0102" },
];

const CLIENTS = [
  "Nike","Jordan","FILA","Umbro","Converse","Vans","Puma","Kappa","DC Shoes","Quiksilver","Merrell","Columbia",
  "Swatch","CAT","Joma","Golty","BodyTech","Kicks","Falabella","Sportline","Planeta Sports","Hush Puppies","Sebago","Cushe",
  "Paul Frank","Páramo","Outdoor Adventure","Grupo Depor","Sport Life","Spring Step","PGA of America","Marriott","Truval","newhome",
  "Dimayor","FCF","FPF","Santa Fe","Junior","Deportivo Cali","América de Cali","Once Caldas",
];

function Ph({ n, ratio = "43", alt, img, fit = "cover", pos = "center", bg, stack }) {
  if (stack) {
    return (
      <div className={`wp-ph wp-r${ratio} wp-ph-stack`} role="img" aria-label={alt} data-testid={`wp-img-${n}`}>
        {stack.map((src) => <img key={src} src={src} alt="" loading="lazy" />)}
      </div>
    );
  }
  if (img) {
    return (
      <div
        className={`wp-ph wp-r${ratio} wp-ph-photo`}
        role="img"
        aria-label={alt}
        data-testid={`wp-img-${n}`}
        style={{ backgroundImage: `url(${img})`, backgroundSize: fit, backgroundPosition: pos, backgroundRepeat: "no-repeat", backgroundColor: bg }}
      />
    );
  }
  return (
    <div className={`wp-ph wp-r${ratio}`} data-testid={`wp-img-${n}`}>
      <span>Image {String(n).padStart(2, "0")}. {alt}</span>
    </div>
  );
}

export default function WilhelmPage() {
  useSeo({
    title: "Wilhelm Noriega — Brand Strategy Advisor & Marketing Director",
    description: "Wilhelm Noriega leads Brand & Behavior from Barcelona. 15+ years directing brands across Nike, Jordan, FILA, Vans, Converse, Columbia, Merrell, Swatch and Quiksilver.",
    path: "/wilhelm-noriega",
  });
  useProfileJsonLd();
  return (
    <main className="app-main wilhelm-page" data-testid="wilhelm-page">
      {/* HERO */}
      <section className="wp-hero">
        <div className="wp-wrap">
          <div className="wp-label">Wilhelm Noriega — Brand Strategy Advisor / Marketing Director</div>
          <h1>
            Brands build as <em className="it">worlds,</em> defined by <em className="it">creativity,</em> guided by <em className="it">strategy.</em>
          </h1>
          <p className="wp-sub">
            I direct brands as living systems: reading culture early, defining the DNA, and turning strategy into images, spaces, products and stories people want to belong to.
          </p>
          <div className="wp-hero-meta">
            <div><span className="wp-n">15+</span><span className="wp-t">Years</span></div>
            <div><span className="wp-n">500+</span><span className="wp-t">Projects Directed</span></div>
            <div><span className="wp-n">40+</span><span className="wp-t">Brands & Institutions</span></div>
          </div>
          <div className="wp-hero-band">
            <Ph n={1} ratio="169" alt="Image 01" img="/images/wilhelm/01.jpg" />
            <Ph n={2} ratio="34" alt="Image 02" img="/images/wilhelm/02.jpg" />
          </div>
        </div>
      </section>

      {/* THE PRACTICE */}
      <section id="practice" className="wp-section" data-testid="wp-practice">
        <div className="wp-wrap">
          <div className="wp-sec-head">
            <span className="wp-pill">The Practice</span>
            <h2>Four disciplines, one <em className="it">system.</em></h2>
            <p>This is how I work: one correct system applied to the brand and protected across every expression, so the brand is built with unity.</p>
          </div>

          {MODULES.map((m) => (
            <article className={`wp-module${m.dark ? " wp-dark" : ""}`} key={m.n} data-testid={`wp-module-${m.n}`}>
              <div className="wp-m-side">
                <span className="wp-m-num">{m.n}</span>
                <h3>{m.title}</h3>
                <span className="wp-m-tag">{m.tag}</span>
              </div>
              <div className="wp-m-copy">
                <p>{m.p1}</p>
                <p>{m.p2}</p>
                <div className="wp-m-imgs">
                  {m.imgs.map((im) => <Ph key={im.n} n={im.n} ratio="43" alt={im.alt} img={im.img} />)}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* POINT OF VIEW */}
      <section id="future" className="wp-section wp-future" data-testid="wp-future">
        <div className="wp-wrap">
          <div className="wp-sec-head">
            <span className="wp-pill">Point of View</span>
            <h2>What I <em className="it">believe.</em></h2>
          </div>
          <blockquote className="wp-belief-quote" data-testid="wp-belief-quote">
            {BELIEF_QUOTE}
          </blockquote>
          <div className="wp-future-list">
            {BELIEFS.map((b) => (
              <div className="wp-future-item" key={b.n}>
                <span className="wp-fn">{b.n}</span>
                <h4>{b.h}</h4>
                <p>{b.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED WORK */}
      <section id="work" className="wp-section" data-testid="wp-work">
        <div className="wp-wrap">
          <div className="wp-sec-head">
            <span className="wp-pill">Selected Work</span>
            <h2>The work, <em className="it">seen.</em></h2>
          </div>
          <div className="wp-work-grid">
            {CASES.map((c) => (
              <figure key={c.n} data-testid={`wp-case-${c.name}`}>
                <Ph n={c.n} ratio="43" alt={c.name} img={c.img} fit={c.fit} pos={c.pos} bg={c.bg} stack={c.stack} />
                <figcaption>
                  <strong>{c.name}</strong>
                  <span>{c.meta}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT WALL */}
      <section id="clients" className="wp-section" data-testid="wp-clients">
        <div className="wp-wrap">
          <div className="wp-sec-head">
            <span className="wp-pill">Clients & Partners</span>
            <h2>500+ projects. One <em className="it">standard.</em></h2>
            <p className="wp-sec-sub"><em className="it">From idea to implementation.</em></p>
            <p className="wp-sec-sub-detail">Working globally with companies, founders, internal teams and agencies.</p>
          </div>
          <div className="wp-wall">
            {CLIENTS.map((c) => <div key={c}>{c}</div>)}
          </div>
        </div>
      </section>

      {/* CLOSING / CONTACT */}
      <section className="wp-closing" id="wilhelm-contact" data-testid="wp-contact">
        <div className="wp-wrap">
          <h2>Strategy gives creativity direction. Creativity makes strategy <em className="it">tangible.</em></h2>
          <div className="wp-contact-row">
            <a className="wp-btn-dark" href="mailto:wilhelm@brandandbehavior.com" data-testid="wp-email">Email Wilhelm</a>
            <a className="wp-btn-line" href="https://www.linkedin.com/in/wilhelmnoriega" target="_blank" rel="noopener noreferrer" data-testid="wp-linkedin">LinkedIn</a>
          </div>
        </div>
      </section>
    </main>
  );
}
